import { useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Calendar, 
  Download, 
  RefreshCw, 
  TrendingUp, 
  Search, 
  MousePointer 
} from 'lucide-react';
import { mockKioskAnalytics, mockKioskDevices } from '../../kiosk/data/mock-kiosk-data';
import { MetricCard } from '../components/MetricCard';
import { AnalyticsChart } from '../components/AnalyticsChart';
import { PopularItemsList } from '../components/PopularItemsList';
import { DistributionChart } from '../components/DistributionChart';
import { AnalyticsMetric, AnalyticsChartData } from '../types';
import { ComingSoonOverlay } from '@/features/dashboard/components/ComingSoonOverlay';

export function AnalyticsPage() {
  const [timeframe, setTimeframe] = useState('7d');
  const [deviceId, setDeviceId] = useState('all');
  
  // Calculate total metrics across all devices
  const totalViews = mockKioskAnalytics.reduce((sum, item) => sum + item.views, 0);
  const totalSearches = mockKioskAnalytics.reduce((sum, item) => sum + item.searches, 0);
  const totalInteractions = mockKioskAnalytics.reduce((sum, item) => sum + item.interactions, 0);
  
  // Combine all popular documents and sort by views
  const allPopularDocuments = mockKioskAnalytics
    .flatMap(item => item.popularDocuments)
    .reduce((acc, item) => {
      const existing = acc.find(i => i.documentId === item.documentId);
      if (existing) {
        existing.views += item.views;
      } else {
        acc.push({ ...item });
      }
      return acc;
    }, [] as { documentId: string; views: number }[])
    .sort((a, b) => b.views - a.views)
    .slice(0, 10);
  
  // Combine all popular search terms and sort by count
  const allPopularSearchTerms = mockKioskAnalytics
    .flatMap(item => item.popularSearchTerms)
    .reduce((acc, item) => {
      const existing = acc.find(i => i.term === item.term);
      if (existing) {
        existing.count += item.count;
      } else {
        acc.push({ ...item });
      }
      return acc;
    }, [] as { term: string; count: number }[])
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);
  
  // Create metrics for the cards
  const viewsMetric: AnalyticsMetric = {
    label: "Total Views",
    value: totalViews,
    change: 8.2,
    trend: 'up'
  };
  
  const searchesMetric: AnalyticsMetric = {
    label: "Total Searches",
    value: totalSearches,
    change: -2.5,
    trend: 'down'
  };
  
  const interactionsMetric: AnalyticsMetric = {
    label: "Total Interactions",
    value: totalInteractions,
    change: 12.7,
    trend: 'up'
  };
  
  // Sample data for charts
  const viewsChartData: AnalyticsChartData = {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [{
      label: 'Views',
      data: [120, 145, 135, 160, 180, 90, 75],
      borderColor: '#3b82f6',
      backgroundColor: 'rgba(59, 130, 246, 0.5)'
    }]
  };
  
  const searchesChartData: AnalyticsChartData = {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [{
      label: 'Searches',
      data: [42, 53, 48, 59, 62, 38, 29],
      borderColor: '#10b981',
      backgroundColor: 'rgba(16, 185, 129, 0.5)'
    }]
  };
  
  const interactionsChartData: AnalyticsChartData = {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [{
      label: 'Interactions',
      data: [85, 102, 91, 120, 130, 65, 55],
      borderColor: '#f59e0b',
      backgroundColor: 'rgba(245, 158, 11, 0.5)'
    }]
  };
  
  // Calculate percentages for popular items
  const totalDocViews = allPopularDocuments.reduce((sum, doc) => sum + doc.views, 0);
  const popularDocuments = allPopularDocuments.map(doc => ({
    id: doc.documentId,
    name: `Document ${doc.documentId}`,
    count: doc.views,
    percentage: Math.round((doc.views / totalDocViews) * 100)
  }));
  
  const totalSearchCount = allPopularSearchTerms.reduce((sum, term) => sum + term.count, 0);
  const popularSearches = allPopularSearchTerms.map(term => ({
    id: term.term,
    name: term.term,
    count: term.count,
    percentage: Math.round((term.count / totalSearchCount) * 100)
  }));
  
  // Distribution data
  const deviceDistribution = [
    { label: 'Lobby iPad', value: 45 },
    { label: 'Historical Society iPad', value: 35 },
    { label: 'HR Department iPad', value: 20 }
  ];
  
  const contentTypeDistribution = [
    { label: 'Archives', value: 50 },
    { label: 'Employees', value: 30 },
    { label: 'Directors', value: 20 }
  ];
  
  return (
    <>
      <PageHeader
        title="Analytics"
        description="View usage statistics and analytics for kiosk devices."
        actions={
          <>
            <Button variant="outline" size="sm">
              <Calendar className="mr-2 h-4 w-4" />
              Date Range
            </Button>
            <Button variant="outline" size="sm">
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
            <Button variant="outline" size="sm">
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </>
        }
      />
      
      <ComingSoonOverlay>
        <div className="space-y-4">
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="w-full md:w-1/2">
              <Select value={timeframe} onValueChange={setTimeframe}>
                <SelectTrigger>
                  <SelectValue placeholder="Select timeframe" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24h">Last 24 Hours</SelectItem>
                  <SelectItem value="7d">Last 7 Days</SelectItem>
                  <SelectItem value="30d">Last 30 Days</SelectItem>
                  <SelectItem value="90d">Last 90 Days</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-full md:w-1/2">
              <Select value={deviceId} onValueChange={setDeviceId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select device" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Devices</SelectItem>
                  {mockKioskDevices.map(device => (
                    <SelectItem key={device.id} value={device.id}>
                      {device.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid gap-4 md:grid-cols-3">
            <MetricCard 
              metric={viewsMetric} 
              icon={<TrendingUp className="h-4 w-4" />}
            />
            
            <MetricCard 
              metric={searchesMetric} 
              icon={<Search className="h-4 w-4" />}
            />
            
            <MetricCard 
              metric={interactionsMetric} 
              icon={<MousePointer className="h-4 w-4" />}
            />
          </div>
          
          <Tabs defaultValue="overview" className="space-y-4">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="content">Content</TabsTrigger>
              <TabsTrigger value="search">Search</TabsTrigger>
              <TabsTrigger value="devices">Devices</TabsTrigger>
            </TabsList>
            
            <TabsContent value="overview">
              <div className="grid gap-4 md:grid-cols-2">
                <AnalyticsChart 
                  title="Views Over Time"
                  description="Daily content views for the selected period"
                  data={viewsChartData}
                />
                
                <AnalyticsChart 
                  title="Interactions Over Time"
                  description="Daily user interactions for the selected period"
                  data={interactionsChartData}
                />
              </div>
            </TabsContent>
            
            <TabsContent value="content">
              <div className="grid gap-4 md:grid-cols-2">
                <PopularItemsList 
                  title="Popular Documents"
                  description="Most viewed documents across all kiosks"
                  items={popularDocuments}
                />
                
                <DistributionChart 
                  title="Content Type Distribution"
                  description="Breakdown of content types viewed"
                  data={contentTypeDistribution}
                />
              </div>
            </TabsContent>
            
            <TabsContent value="search">
              <div className="grid gap-4 md:grid-cols-2">
                <PopularItemsList 
                  title="Popular Search Terms"
                  description="Most frequently searched terms"
                  items={popularSearches}
                />
                
                <AnalyticsChart 
                  title="Searches Over Time"
                  description="Daily search volume for the selected period"
                  data={searchesChartData}
                />
              </div>
            </TabsContent>
            
            <TabsContent value="devices">
              <div className="grid gap-4 md:grid-cols-2">
                <DistributionChart 
                  title="Device Usage"
                  description="Distribution of usage across devices"
                  data={deviceDistribution}
                />
                
                <Card>
                  <CardHeader>
                    <CardTitle>Device Status</CardTitle>
                    <CardDescription>
                      Current status of all kiosk devices
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {mockKioskDevices.map(device => (
                        <div key={device.id} className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className={`w-3 h-3 rounded-full mr-3 ${
                              device.status === 'online' ? 'bg-green-500' : 
                              device.status === 'offline' ? 'bg-red-500' : 'bg-gray-500'
                            }`} />
                            <div>
                              <div className="font-medium">{device.name}</div>
                              <div className="text-sm text-muted-foreground">{device.location}</div>
                            </div>
                          </div>
                          <div className="text-sm">
                            {device.status === 'online' ? 'Online' : 
                             device.status === 'offline' ? 'Offline' : 'Unknown'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </ComingSoonOverlay>
    </>
  );
}

export default AnalyticsPage;
